import { SpecError } from "../errors";
import type {
  ConversionStats,
  ConvertedComponentRefTarget,
  ConvertedComponentTarget,
  ConvertedIo,
  ConvertedItemBehavior,
  ConvertedItemCategoryEntry,
  ConvertedItemDbMapping,
  ConvertedItemMessage,
  ConvertedItemTarget,
  ConvertedItemTransition,
  ConvertedItemValidation,
  ConvertedOverlayTarget,
  ConvertedSectionTarget,
  ConvertedServiceTarget,
  ConvertedTarget,
  ConvertedViewTarget,
} from "../types";
import type { WarningCollector } from "../warnings/warning-collector";
import type {
  ParsedItem,
  ParsedItemContext,
  ParsedSpec,
} from "../parser/types";
import { uuidv7 } from "uuidv7";
import { IdTranslator } from "./id-translator";
import { LegendMapper, type LegendMappingEntry } from "./legend-mapper";

/**
 * Phase 3: `ParsedSpec` を `ConvertedTarget[]` に変換し、最後に `IdTranslator` で
 * 内部 UUID を spec-id ベースの合成キーに置換する。
 */
export class TargetBuilder {
  private readonly legend = new LegendMapper();
  private readonly idTranslator = new IdTranslator();

  constructor(private readonly warnings: WarningCollector) {}

  build(parsed: ParsedSpec): { targets: ConvertedTarget[]; stats: ConversionStats } {
    const targets: ConvertedTarget[] = [];
    const targetById = new Map<string, ConvertedTarget>();
    const pushTarget = (target: ConvertedTarget): void => {
      targets.push(target);
      targetById.set(target.id, target);
    };

    const serviceIdMap = new Map<string, string>();
    const viewIdMap = new Map<string, string>();
    const componentIdMap = new Map<string, string>();
    const serviceSpecIdsSeen = new Set<string>();
    const viewSpecIdsSeen = new Set<string>();
    const componentSpecIdsSeen = new Set<string>();

    // specId 欠落 / 重複は target 化不可能 (一意に識別できない) として skip + warning。
    // ユーザーは warning を見て仕様書を修正し再インポートする運用 (design.md §3 / §7 参照)。
    const checkSpecId = (
      specId: string,
      seen: Set<string>,
      type: "service" | "view" | "component",
      name: string,
      sheetName: string,
      sourceRow: number,
    ): boolean => {
      const columnLabel = type === "service" ? "サービスID" : type === "view" ? "画面ID" : "共通コンポーネントID";
      const typeLabel = type === "service" ? "Service" : type === "view" ? "View" : "Component";
      if (!specId) {
        this.warnings.add({
          code: "missing_spec_id",
          message: `${columnLabel} is empty; skipping ${typeLabel} "${name}".`,
          location: { sheetName, rowIndex: sourceRow, columnName: columnLabel },
          context: { type, name },
        });
        return false;
      }
      if (seen.has(specId)) {
        this.warnings.add({
          code: "duplicate_spec_id",
          message: `${columnLabel}="${specId}" is duplicated; skipping the later row (${typeLabel} "${name}") and keeping the first occurrence.`,
          location: { sheetName, rowIndex: sourceRow, columnName: columnLabel },
          context: { type, specId, name },
        });
        return false;
      }
      seen.add(specId);
      return true;
    };

    for (const s of parsed.services) {
      if (!checkSpecId(s.specId, serviceSpecIdsSeen, "service", s.name, "サービスリスト", s.sourceRow)) {
        continue;
      }
      const id = uuidv7();
      const target: ConvertedServiceTarget = {
        id,
        type: "service",
        name: s.name,
        specSource: {
          sheetName: "サービスリスト",
          rowIndex: s.sourceRow,
          specId: s.specId,
        },
        params: {
          ...(s.baseUrl ? { baseUrl: s.baseUrl } : {}),
          description: s.description,
        },
      };
      pushTarget(target);
      serviceIdMap.set(s.specId, id);
    }

    for (const v of parsed.views) {
      // checkSpecId を先に実行し specId 妥当性検査を独立させる。
      // unknown_service で先に弾くと specId 重複検出 (`viewSpecIdsSeen`) に穴ができるため。
      if (!checkSpecId(v.specId, viewSpecIdsSeen, "view", v.name, "画面リスト", v.sourceRow)) {
        continue;
      }
      const parentId = serviceIdMap.get(v.serviceSpecId);
      if (!parentId) {
        this.warnings.add({
          code: "unknown_service",
          message: `Parent サービスID="${v.serviceSpecId}" of View "${v.name}" not found in サービスリスト; skipping this View.`,
          location: { sheetName: "画面リスト", rowIndex: v.sourceRow, columnName: "サービスID" },
          context: { viewSpecId: v.specId, missingServiceSpecId: v.serviceSpecId },
        });
        continue;
      }
      const id = uuidv7();
      const target: ConvertedViewTarget = {
        id,
        parentId,
        type: "view",
        name: v.name,
        specSource: {
          sheetName: "画面リスト",
          rowIndex: v.sourceRow,
          specId: v.specId,
        },
        params: {
          paths: [...v.paths],
          description: v.description,
        },
      };
      pushTarget(target);
      viewIdMap.set(v.specId, id);
    }

    for (const c of parsed.components) {
      if (!checkSpecId(c.specId, componentSpecIdsSeen, "component", c.name, "共通コンポーネントリスト", c.sourceRow)) {
        continue;
      }
      const parentId = serviceIdMap.get(c.serviceSpecId);
      if (!parentId) {
        this.warnings.add({
          code: "unknown_service",
          message: `Parent サービスID="${c.serviceSpecId}" of Component "${c.name}" not found in サービスリスト; skipping this Component.`,
          location: { sheetName: "共通コンポーネントリスト", rowIndex: c.sourceRow, columnName: "サービスID" },
          context: { componentSpecId: c.specId, missingServiceSpecId: c.serviceSpecId },
        });
        continue;
      }
      const id = uuidv7();
      const target: ConvertedComponentTarget = {
        id,
        parentId,
        type: "component",
        name: c.name,
        specSource: {
          sheetName: "共通コンポーネントリスト",
          rowIndex: c.sourceRow,
          specId: c.specId,
        },
        params: {
          description: c.description,
        },
      };
      pushTarget(target);
      componentIdMap.set(c.specId, id);
    }

    // ctxKey = `${kind}:${contextId}` で view / component の ID 名前空間を分離。
    const itemIdByContextByNo = new Map<string, Map<string, string>>();
    const itemIdByContextByName = new Map<string, Map<string, string>>();
    // ctxKey → シート内で名前が重複した item 名の集合。親項目参照がこの名前を指すと曖昧解決になる。
    const ambiguousNamesByContext = new Map<string, Set<string>>();
    const seenContextKeys = new Map<string, string>(); // ctxKey → 採用した sheetName

    for (const ctx of parsed.itemsByContext) {
      const parentId =
        ctx.contextKind === "view"
          ? viewIdMap.get(ctx.contextId)
          : componentIdMap.get(ctx.contextId);
      if (!parentId) {
        this.warnings.add({
          code: ctx.contextKind === "view" ? "unknown_screen_id" : "unknown_common_component_id",
          message:
            ctx.contextKind === "view"
              ? `画面ID="${ctx.contextId}" in sheet "${ctx.sheetName}" not found in 画面リスト; skipping all items under this sheet.`
              : `共通コンポーネントID="${ctx.contextId}" in sheet "${ctx.sheetName}" not found in 共通コンポーネントリスト; skipping all items under this sheet.`,
          location: { sheetName: ctx.sheetName },
          context: { sheetName: ctx.sheetName, contextId: ctx.contextId, contextKind: ctx.contextKind },
        });
        continue;
      }

      const ctxKey = `${ctx.contextKind}:${ctx.contextId}`;
      // 複数の詳細シートが同じ contextId を宣言した場合、後続シートは silent overwrite せず skip + warning。
      const firstSheet = seenContextKeys.get(ctxKey);
      if (firstSheet) {
        this.warnings.add({
          code: "duplicate_detail_sheet_for_context",
          message: `Multiple detail sheets declare the same contextId="${ctx.contextId}" (kind=${ctx.contextKind}); keeping "${firstSheet}" and skipping "${ctx.sheetName}".`,
          location: { sheetName: ctx.sheetName },
          context: { contextKind: ctx.contextKind, contextId: ctx.contextId, firstSheet, skippedSheet: ctx.sheetName },
        });
        continue;
      }
      seenContextKeys.set(ctxKey, ctx.sheetName);

      if (ctx.contextKind === "view" && ctx.conditions.length > 0) {
        const viewTarget = targetById.get(parentId);
        if (viewTarget?.type === "view") {
          viewTarget.params.conditions = [...ctx.conditions];
        }
      }

      const noMap = new Map<string, string>();
      const nameMap = new Map<string, string>();
      const ambiguousNames = new Set<string>();
      itemIdByContextByNo.set(ctxKey, noMap);
      itemIdByContextByName.set(ctxKey, nameMap);
      ambiguousNamesByContext.set(ctxKey, ambiguousNames);

      for (const item of ctx.items) {
        const legendEntries = this.legend.mapAll(item.objectType);
        const legendType = firstMappedEntry(legendEntries)?.mapping?.type;
        let id: string | null;
        if (legendType === "component-ref") {
          id = this.buildComponentRefTarget(item, ctx, parentId, componentIdMap, pushTarget);
        } else {
          id = this.buildItemLikeTarget(item, ctx, parentId, pushTarget, legendEntries);
        }
        if (id === null) continue; // unresolved component-ref はスキップ
        noMap.set(item.no, id);
        if (nameMap.has(item.name)) {
          ambiguousNames.add(item.name);
          this.warnings.add({
            code: "duplicate_item_name_in_sheet",
            message: `Item name "${item.name}" is duplicated in the sheet; parentItemRef lookups resolve to the last occurrence (last-wins).`,
            location: { sheetName: ctx.sheetName, rowIndex: item.sourceRow, columnName: "項目名" },
            context: { itemName: item.name, contextId: ctx.contextId },
          });
        }
        nameMap.set(item.name, id);
      }
    }

    // パス 2A: 全 context の親項目参照を「仮想グラフ」に集める (確定はせず、cycle 検出のため)
    // childId → { parentId, ctx, item }
    type ProposedEdge = { parentId: string; ctx: ParsedItemContext; item: ParsedItem };
    const proposedParentEdges = new Map<string, ProposedEdge>();
    for (const ctx of parsed.itemsByContext) {
      const ctxKey = `${ctx.contextKind}:${ctx.contextId}`;
      const noMap = itemIdByContextByNo.get(ctxKey);
      const nameMap = itemIdByContextByName.get(ctxKey);
      const ambiguousNames = ambiguousNamesByContext.get(ctxKey);
      if (!noMap || !nameMap) continue;
      for (const item of ctx.items) {
        if (!item.parentItemRef) continue;
        const childId = noMap.get(item.no);
        if (!childId) continue;
        const childTarget = targetById.get(childId);
        if (!childTarget) continue;
        if (childTarget.type !== "item" && childTarget.type !== "component-ref") {
          this.warnings.add({
            code: "invalid_parent_ref",
            message: `${childTarget.type} row "${item.name}" has 親項目 "${item.parentItemRef}", but section / overlay must be a direct child of View / Component; ignoring 親項目.`,
            location: { sheetName: ctx.sheetName, rowIndex: item.sourceRow, columnName: "親項目" },
            context: { itemName: item.name, targetType: childTarget.type, parentItemRef: item.parentItemRef },
          });
          continue;
        }
        // 親項目が同名複数にマッチする場合、last-wins による誤配置を防ぐため致命的エラー。
        if (ambiguousNames?.has(item.parentItemRef)) {
          const err = new SpecError(
            "AMBIGUOUS_PARENT_ITEM_REF",
            `親項目 "${item.parentItemRef}" of "${item.name}" is ambiguous: multiple items share this name in sheet "${ctx.sheetName}". Rename so parent references resolve uniquely.`,
            { itemName: item.name, parentItemRef: item.parentItemRef, sheetName: ctx.sheetName, contextId: ctx.contextId },
          );
          err.warnings = this.warnings.collect();
          throw err;
        }
        const parentItemId = nameMap.get(item.parentItemRef);
        if (!parentItemId) {
          this.warnings.add({
            code: "unresolved_parent_item_ref",
            message: `親項目 "${item.parentItemRef}" of item "${item.name}" not found in the sheet; treating as a direct child of View / Component.`,
            location: { sheetName: ctx.sheetName, rowIndex: item.sourceRow, columnName: "親項目" },
            context: { itemName: item.name, parentItemRef: item.parentItemRef, contextId: ctx.contextId },
          });
          continue;
        }
        // component-ref の親項目が item を指す場合は致命的エラー
        if (childTarget.type === "component-ref") {
          const parentTarget = targetById.get(parentItemId);
          if (parentTarget?.type === "item") {
            const err = new SpecError(
              "COMPONENT_REF_INVALID_PLACEMENT",
              `component-ref row "${item.name}" has 親項目 "${item.parentItemRef}" which resolves to an item-type target; component-ref may only be placed under View / Component or section / overlay.`,
              { itemName: item.name, parentItemRef: item.parentItemRef, parentTargetType: "item" },
            );
            err.warnings = this.warnings.collect();
            throw err;
          }
        }
        proposedParentEdges.set(childId, { parentId: parentItemId, ctx, item });
      }
    }

    // パス 2B: 仮想グラフで cycle 検出 (3 段以上の循環も網羅)
    const cycleMembers = detectCyclesInProposedEdges(proposedParentEdges);

    // パス 2C: cycle に含まれない edge のみ確定。cycle メンバーは警告 emit + 親付け替えスキップ
    for (const [childId, edge] of Array.from(proposedParentEdges)) {
      if (cycleMembers.has(childId)) {
        this.warnings.add({
          code: "circular_parent_ref",
          message: `Circular 親項目 reference ("${edge.item.name}" → "${edge.item.parentItemRef}"); skipping parentId for cycle members (treated as direct children of View / Component).`,
          location: { sheetName: edge.ctx.sheetName, rowIndex: edge.item.sourceRow, columnName: "親項目" },
          context: { itemName: edge.item.name, parentItemRef: edge.item.parentItemRef },
        });
        continue;
      }
      const childTarget = targetById.get(childId);
      if (childTarget && (childTarget.type === "item" || childTarget.type === "component-ref")) {
        childTarget.parentId = edge.parentId;
      }
    }

    // パス 2D: transitions / component-ref 参照解決 (context ごとに処理)
    for (const ctx of parsed.itemsByContext) {
      const ctxKey = `${ctx.contextKind}:${ctx.contextId}`;
      const noMap = itemIdByContextByNo.get(ctxKey);
      if (!noMap) continue;

      for (const item of ctx.items) {
        if (item.transitions.length === 0) continue;
        const itemId = noMap.get(item.no);
        if (!itemId) continue;
        const target = targetById.get(itemId);
        if (!target) continue;
        // transitions は item のみサポート。section / overlay は UI 未対応のため警告して破棄。
        if (target.type !== "item") {
          this.warnings.add({
            code: "invalid_field_on_non_item_type",
            message: `${target.type} row "${target.name}" has 画面遷移, but it is not supported on section / overlay; discarding 画面遷移.`,
            location: { sheetName: ctx.sheetName, rowIndex: item.sourceRow, columnName: "画面ID" },
            context: { targetType: target.type, itemName: item.name, field: "transitions" },
          });
          continue;
        }

        const resolved: ConvertedItemTransition[] = [];
        for (const tr of item.transitions) {
          const targetId = viewIdMap.get(tr.targetScreenId);
          const mappedTransitionType = this.legend.lookupTransitionType(tr.transitionType);
          if (tr.transitionType && !mappedTransitionType) {
            this.warnings.add({
              code: "unknown_transition_type",
              message: `遷移タイプ "${tr.transitionType}" not found in legend; registered as same_tab.`,
              location: { sheetName: ctx.sheetName, rowIndex: item.sourceRow, columnName: "遷移タイプ" },
              context: { transitionType: tr.transitionType, itemName: item.name },
            });
          }
          const transitionType = mappedTransitionType ?? "same_tab";
          if (!targetId) {
            this.warnings.add({
              code: "unresolved_screen_ref",
              message: `画面ID="${tr.targetScreenId}" (${tr.targetScreenName}) in 画面遷移 not found in 画面リスト; skipping this transition.`,
              location: { sheetName: ctx.sheetName, rowIndex: item.sourceRow, columnName: "画面ID" },
              context: { targetScreenId: tr.targetScreenId, targetScreenName: tr.targetScreenName },
            });
            continue;
          }
          resolved.push({ targetId, transitionType, condition: tr.condition });
        }
        if (resolved.length > 0) {
          target.params.transitions = resolved;
        }
      }

    }

    // 同一階層 (同一 parentId + type + trim済み name) の重複を致命的エラーとして検出。
    // name 照合による更新/新規/削除の差分同期で照合先が曖昧になることを防ぐ。
    const siblingKey = (t: ConvertedTarget): string =>
      `${t.parentId ?? ""}|${t.type}|${t.name.trim()}`;
    const seenSiblingKeys = new Map<string, ConvertedTarget>();
    const seenFirstKeys = new Set<string>();
    const duplicateEntries: { name: string; sheetName: string; rowIndex?: number }[] = [];
    for (const t of targets) {
      // name が空文字の場合は重複検出をスキップ（空 name が複数あると false-positive になるため）
      if (t.name.trim() === "") continue;
      const key = siblingKey(t);
      const first = seenSiblingKeys.get(key);
      if (first) {
        if (!seenFirstKeys.has(key)) {
          seenFirstKeys.add(key);
          duplicateEntries.push({
            name: first.name.trim(),
            sheetName: first.specSource.sheetName,
            rowIndex: first.specSource.rowIndex,
          });
        }
        duplicateEntries.push({
          name: t.name.trim(),
          sheetName: t.specSource.sheetName,
          rowIndex: t.specSource.rowIndex,
        });
      } else {
        seenSiblingKeys.set(key, t);
      }
    }
    if (duplicateEntries.length > 0) {
      const detail = duplicateEntries
        .map(
          (e) =>
            `"${e.name}" (sheet: ${e.sheetName}${e.rowIndex != null ? `, row: ${e.rowIndex}` : ""})`,
        )
        .join(", ");
      const err = new SpecError(
        "DUPLICATE_NAME_AT_SAME_LEVEL",
        `Duplicate names found at the same level: ${detail}`,
        { duplicates: duplicateEntries },
      );
      err.warnings = this.warnings.collect();
      throw err;
    }

    const translated = this.idTranslator.translate(targets);
    return { targets: translated, stats: computeStats(translated) };
  }

  /** Item / Section / Overlay を生成して targets に push し、内部 UUID を返す。 */
  private buildItemLikeTarget(
    item: ParsedItem,
    ctx: ParsedItemContext,
    parentId: string,
    pushTarget: (target: ConvertedTarget) => void,
    legendEntries = this.legend.mapAll(item.objectType),
  ): string {
    this.warnUnknownObjectTypes(item, ctx, legendEntries);
    const mapping = firstMappedEntry(legendEntries)?.mapping;
    if (!mapping && legendEntries.length === 0) {
      this.warnings.add({
        code: "unknown_object_type",
        message: `オブジェクト種別 is empty; registered as Item (項目名: ${item.name}).`,
        location: { sheetName: ctx.sheetName, rowIndex: item.sourceRow, columnName: "オブジェクト種別" },
        context: { objectType: item.objectType, itemName: item.name },
      });
    }
    const targetType = mapping?.type ?? "item";
    const id = uuidv7();
    const specSource = {
      sheetName: ctx.sheetName,
      rowIndex: item.sourceRow,
      itemNo: item.no,
    };

    const commonParams = {
      description: item.description,
      ...(item.displayText ? { displayText: item.displayText } : {}),
      ...(item.formCategory ? { formCategory: item.formCategory } : {}),
      ...(item.conditions.length > 0 ? { conditions: [...item.conditions] } : {}),
      ...this.buildIo(item.io),
      ...this.buildIsTemplate(item.isTemplate),
      ...this.buildDbMapping(item.dbMapping),
      ...this.buildValidation(item.validation),
      ...this.buildBehaviors(item.behaviors),
      ...this.buildMessages(item.messages),
    };

    if (targetType === "section") {
      const target: ConvertedSectionTarget = {
        id, parentId, type: "section", name: item.name, specSource,
        params: commonParams,
      };
      pushTarget(target);
      return id;
    }
    if (targetType === "overlay") {
      const target: ConvertedOverlayTarget = {
        id, parentId, type: "overlay", name: item.name, specSource,
        params: commonParams,
      };
      pushTarget(target);
      return id;
    }
    // item
    const itemCategories = buildItemCategoryEntries(item, legendEntries);
    const primaryCategory = itemCategories[0];
    const itemCategory = primaryCategory?.itemCategory;
    const itemSubCategory = primaryCategory?.itemSubCategory;
    const objectTypes = legendEntries.map((entry) => entry.objectType);
    const target: ConvertedItemTarget = {
      id, parentId, type: "item", name: item.name, specSource,
      params: {
        ...commonParams,
        objectType: item.objectType,
        ...(objectTypes.length > 1 ? { objectTypes } : {}),
        ...(itemCategory ? { itemCategory } : {}),
        ...(itemSubCategory ? { itemSubCategory } : {}),
        ...(itemCategories.length > 1 ? { itemCategories } : {}),
      },
    };
    pushTarget(target);
    return id;
  }

  private warnUnknownObjectTypes(
    item: ParsedItem,
    ctx: ParsedItemContext,
    legendEntries: LegendMappingEntry[],
  ): void {
    for (const entry of legendEntries) {
      if (entry.mapping) continue;
      this.warnings.add({
        code: "unknown_object_type",
        message: `オブジェクト種別="${entry.objectType}" not found in legend; registered as Item (項目名: ${item.name}).`,
        location: { sheetName: ctx.sheetName, rowIndex: item.sourceRow, columnName: "オブジェクト種別" },
        context: { objectType: entry.objectType, itemName: item.name },
      });
    }
  }

  /**
   * component-ref ターゲットを生成して targets に push し、内部 UUID を返す。
   * 参照先コンポーネントが未解決の場合は warning を emit して null を返す (呼び元でスキップ)。
   */
  private buildComponentRefTarget(
    item: ParsedItem,
    ctx: ParsedItemContext,
    parentId: string,
    componentIdMap: Map<string, string>,
    pushTarget: (target: ConvertedTarget) => void,
  ): string | null {
    const refSpecId = item.referenceComponentId ?? "";
    const componentId = componentIdMap.get(refSpecId);
    if (!componentId) {
      this.warnings.add({
        code: "unresolved_component_ref",
        message: `参照コンポーネント="${refSpecId}" not found in 共通コンポーネントリスト; skipping this placement.`,
        location: { sheetName: ctx.sheetName, rowIndex: item.sourceRow, columnName: "参照コンポーネント" },
        context: { referenceComponentId: refSpecId },
      });
      return null;
    }
    const id = uuidv7();
    const specSource = {
      sheetName: ctx.sheetName,
      rowIndex: item.sourceRow,
      itemNo: item.no,
    };
    const target: ConvertedComponentRefTarget = {
      id,
      parentId,
      type: "component-ref",
      name: item.name,
      specSource,
      params: {
        referenceTargetId: componentId,
        conditions: [...item.conditions],
        description: item.description ?? "",
      },
    };
    pushTarget(target);
    return id;
  }

  private buildIo(io: string | undefined): { io?: ConvertedIo } {
    if (!io) return {};
    const mapped = this.legend.lookupIo(io);
    return mapped ? { io: mapped } : {};
  }

  private buildIsTemplate(isTemplate: boolean | undefined) {
    return isTemplate === undefined ? {} : { isTemplate };
  }

  private buildDbMapping(dbMapping: ConvertedItemDbMapping[]) {
    return dbMapping.length > 0 ? { dbMapping } : {};
  }

  /**
   * 上流の parser で各フィールドは `parseBool` / `optional` 経由で undefined 化済み。
   * 全フィールドが undefined なら validation 自体を出力しない。
   */
  private buildValidation(v: ConvertedItemValidation | undefined) {
    if (!v) return {};
    const hasAny =
      v.required !== undefined ||
      !!v.defaultValue ||
      !!v.minLength ||
      !!v.maxLength ||
      !!v.pattern;
    return hasAny ? { validation: v } : {};
  }

  private buildBehaviors(behaviors: ConvertedItemBehavior[]) {
    return behaviors.length > 0 ? { behaviors } : {};
  }

  private buildMessages(messages: ConvertedItemMessage[]) {
    return messages.length > 0 ? { messages } : {};
  }
}

function computeStats(targets: ConvertedTarget[]): ConversionStats {
  const stats: ConversionStats = {
    serviceCount: 0,
    viewCount: 0,
    componentCount: 0,
    sectionCount: 0,
    overlayCount: 0,
    itemCount: 0,
  };
  for (const t of targets) {
    switch (t.type) {
      case "service": stats.serviceCount++; break;
      case "view": stats.viewCount++; break;
      case "component": stats.componentCount++; break;
      case "section": stats.sectionCount++; break;
      case "overlay": stats.overlayCount++; break;
      case "item": stats.itemCount++; break;
    }
  }
  return stats;
}

/**
 * 仮想 parent edge map (`childId → parentId`) で循環に含まれる全ノードを返す。
 * 3 段以上 (A→B→C→A) の循環も検出する。
 */
function detectCyclesInProposedEdges(
  edges: ReadonlyMap<string, { parentId: string }>,
): Set<string> {
  const cycleMembers = new Set<string>();
  for (const startKey of Array.from(edges.keys())) {
    if (cycleMembers.has(startKey)) continue;
    const path: string[] = [];
    let cursor: string | undefined = startKey;
    while (cursor && !cycleMembers.has(cursor)) {
      const seenAt = path.indexOf(cursor);
      if (seenAt !== -1) {
        for (let i = seenAt; i < path.length; i++) cycleMembers.add(path[i]);
        break;
      }
      path.push(cursor);
      cursor = edges.get(cursor)?.parentId;
    }
  }
  return cycleMembers;
}

function refineItemSubCategory(
  item: ParsedItem,
  itemCategory: string | undefined,
  mappedSubCategory: string | undefined,
): string | undefined {
  if (itemCategory !== "Input" || mappedSubCategory !== "text") {
    return mappedSubCategory;
  }

  const haystack = [
    item.name,
    item.displayText,
    item.description,
    item.validation?.pattern,
    item.validation?.defaultValue,
    item.validation?.minLength,
    item.validation?.maxLength,
    ...item.dbMapping.flatMap((m) => [m.table, m.column]),
  ]
    .filter((v): v is string => !!v)
    .join(" ");

  if (/(電話番号|tel|tell|phone)/i.test(haystack)) return "tel";
  if (/(メールアドレス|mail|email|e-mail)/i.test(haystack)) return "email";
  if (/(パスワード|password)/i.test(haystack)) return "password";
  if (/(生年月日|宿泊日|日付|年月日|date|birthday|\[0-9\]\{4\}[-/年])/.test(haystack)) return "date";
  if (/(宿泊数|人数|数量|個数|件数|金額|価格|料金|合計|number|amount|price|count|quantity)/i.test(haystack)) {
    return "number";
  }

  return mappedSubCategory;
}

function firstMappedEntry(entries: LegendMappingEntry[]): LegendMappingEntry | undefined {
  return entries.find((entry) => entry.mapping);
}

function buildItemCategoryEntries(
  item: ParsedItem,
  legendEntries: LegendMappingEntry[],
): ConvertedItemCategoryEntry[] {
  const seen = new Set<string>();
  const result: ConvertedItemCategoryEntry[] = [];
  for (const entry of legendEntries) {
    const mapping = entry.mapping;
    if (mapping?.type !== "item" || !mapping.itemCategory) continue;
    const itemSubCategory = refineItemSubCategory(
      item,
      mapping.itemCategory,
      mapping.itemSubCategory,
    );
    const key = `${mapping.itemCategory}|${itemSubCategory ?? ""}|${entry.objectType}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({
      objectType: entry.objectType,
      itemCategory: mapping.itemCategory,
      ...(itemSubCategory ? { itemSubCategory } : {}),
    });
  }
  return result;
}
